import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { getLocale } from '../../utils/locale';
import { PageWrap } from '../../components/Page/Page';
import { OrderlessTask } from '../../components/tasks/OrderlessTask/OrderlessTask';
import { OrderlessCustomerFormSchema } from '../../components/tasks/OrderlessTask/OrderlessTaskSchema';
import { Flex } from '@repo/ui/Flex';
import { Layout } from '../../components/Layout/Layout';
import { createOrderlessReturnMutationFn } from '../../graphql/mutation/createOrderlessReturn';
import { useToast } from '@repo/ui/Toast/ToastRoot';
import { clientLogger } from '../../utils/logger';
import { transformToGraphQLInput } from '../../utils/orderlessTransform';
// Using URL param to signal completion instead of global provider

const OrderlessFormSchema = z.object({
  orderlessCustomer: OrderlessCustomerFormSchema,
});

type OrderlessFormData = z.infer<typeof OrderlessFormSchema>;

export const OrderlessLayout = () => {
  const { errorToast } = useToast();
  const navigate = useNavigate();
  const { retailer } = useParams({ from: '/$retailer/orderless' });
  const { language, country } = getLocale();

  const methods = useForm<OrderlessFormData>({
    resolver: zodResolver(OrderlessFormSchema),
    defaultValues: {
      orderlessCustomer: {
        email: '',
        firstName: '',
        lastName: '',
        address: {
          address1: '',
          address2: '',
          city: '',
          province: '',
          postalCode: '',
          countryCode: country,
        },
        language: language,
        currencyCode: country === 'CA' ? 'CAD' : 'USD',
        phone: '',
        phoneExtension: '',
      },
    },
  });

  const { mutate: createOrderlessReturn, isPending } = useMutation({
    mutationFn: createOrderlessReturnMutationFn,
    onSuccess: (data) => {
      if (data.createOrderlessReturn.success) {
        // Navigate directly to completion page
        navigate({
          to: '/$retailer/complete',
          params: { retailer },
          search: (prev) => ({
            ...prev,
            complete: 'true',
          }),
          replace: true,
        });
      } else {
        const error = data.createOrderlessReturn.errorResponse;
        errorToast({
          message: error?.errorMessage || 'Failed to create return request',
        });
      }
    },
    onError: (error) => {
      errorToast({
        message:
          'There was a problem creating your return request. Please try again.',
      });
      clientLogger.logError(error);
    },
  });

  const handleNext = methods.handleSubmit(async (formData) => {
    const { orderlessCustomer } = formData;

    // Use centralized transformation utility to map form data to GraphQL input
    const input = transformToGraphQLInput(orderlessCustomer);

    createOrderlessReturn(input);
  });

  return (
    <PageWrap>
      <Layout>
        <Flex grow="1" direction="column">
          <FormProvider {...methods}>
            <OrderlessTask handleNext={handleNext} isLoading={isPending} />
          </FormProvider>
        </Flex>
      </Layout>
    </PageWrap>
  );
};
